import { type ReactElement, useState } from 'react';
import { type FilterState, stripPersonalValues } from '@vello/analytics-semantic';
import { type AnalyticsBiCatalogView, type AnalyticsBiFilterSet } from '@vello/api-client';
import { Badge, Button, CheckRow, EmptyState, Field, Notice, SectionTitle, TextInput, Stack, Text, Row, Box, Ink, Measure } from '@vello/web-theme';
import { type BiFilterState, activeFilterState, conditionCount, filterRequestParts, stateFromParts } from '../../../src/index';
import { FormDialog } from '../form-dialog';
import { useConsole } from '../../console';
import { BiProblem, type BiText } from './bi-ui';
import { useFilterSets } from './bi-filter-sets-store';

/** The server's state of a builder state (filled conditions only; HAVING only where the screen has it). */
export function toServerState(state: BiFilterState, catalog: AnalyticsBiCatalogView, allowHaving: boolean): FilterState {
  const active = activeFilterState(allowHaving ? state : { ...state, having: [] }, catalog);
  // A set never holds a person's value (a key, an id, a text): the server refuses one, so it is left out here and the hint says so.
  const { state: clean } = stripPersonalValues(filterRequestParts(active));
  return clean as FilterState;
}

/**
 * Filter sets (D528): the operator's saved conditions - "high ticket", "outside hours" - kept on the server, applied to the builder in one
 * click, optionally shared with the team (read-only for everybody else, and only with colleagues whose reach covers every market the set
 * names). A set never holds a person's value: the screen strips them before it saves, and the server refuses them if it is not told.
 */
export function FilterSetsMenu({
  catalog,
  text,
  current,
  allowHaving,
  onApply,
  idPrefix,
}: {
  catalog: AnalyticsBiCatalogView;
  text: BiText;
  current: BiFilterState;
  allowHaving: boolean;
  onApply: (state: BiFilterState, set: AnalyticsBiFilterSet) => void;
  idPrefix: string;
}): ReactElement {
  const { t } = text;
  const { confirm, toast } = useConsole();
  const store = useFilterSets();
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<AnalyticsBiFilterSet | undefined>(undefined);
  const [newName, setNewName] = useState('');
  const sets = store.state.status === 'ready' ? store.state.value : [];
  const stored = toServerState(current, catalog, allowHaving);
  const nothing = conditionCount(activeFilterState(current, catalog)) === 0 && (!allowHaving || current.having.length === 0);

  const fail = (messageId: string): void => setProblem(t(messageId));
  const save = async (): Promise<void> => {
    setBusy(true);
    setProblem(undefined);
    const res = await store.add({ name, state: stored, shared });
    setBusy(false);
    if (!res.ok) return fail(res.error.messageId);
    setName('');
    setShared(false);
    toast({ tone: 'success', message: t('bi.sets.saved', { name: res.value.name }) });
  };
  const replace = async (set: AnalyticsBiFilterSet): Promise<void> => {
    const res = await store.update(set.id, { state: stored });
    toast(res.ok ? { tone: 'success', message: t('bi.sets.updated', { name: set.name }) } : { tone: 'warning', message: t(res.error.messageId) });
  };
  const share = async (set: AnalyticsBiFilterSet): Promise<void> => {
    const res = await store.update(set.id, { shared: !set.shared });
    toast(res.ok ? { tone: 'success', message: t(res.value.shared ? 'bi.sets.shared' : 'bi.sets.unshared', { name: set.name }) } : { tone: 'warning', message: t(res.error.messageId) });
  };
  const askDelete = async (set: AnalyticsBiFilterSet): Promise<void> => {
    const answer = await confirm({ title: t('bi.sets.delete.title', { name: set.name }), body: t('bi.sets.delete.body'), confirmLabel: t('bi.sets.delete.confirm'), cancelLabel: t('ui.cancel'), tone: 'danger' });
    if (!answer.confirmed) return;
    const res = await store.remove(set.id);
    toast(res.ok ? { tone: 'success', message: t('bi.sets.deleted', { name: set.name }) } : { tone: 'warning', message: t(res.error.messageId) });
  };
  const submitRename = async (): Promise<void> => {
    if (renaming === undefined) return;
    setBusy(true);
    const res = await store.update(renaming.id, { name: newName });
    setBusy(false);
    if (!res.ok) return fail(res.error.messageId);
    setRenaming(undefined);
    toast({ tone: 'success', message: t('bi.sets.renamed', { name: res.value.name }) });
  };

  return (
    <Stack gap={3}>
      <SectionTitle level={3} size="sub">{t('bi.sets.title')}</SectionTitle>
      {store.state.status === 'error' && <BiProblem error={store.state.error} onRetry={store.reload} text={text} />}
      {store.state.status === 'loading' && <Text role="caption" tone="secondary">{t('ui.loading')}</Text>}
      {store.state.status === 'ready' && sets.length === 0 && <EmptyState title={t('bi.sets.empty')} detail={store.canSave ? t('bi.sets.empty.hint') : undefined} />}
      {sets.length > 0 && (
        <Stack as="ul" gap={2} aria-label={t('bi.sets.list')}>
          {sets.map((set) => {
            const state = stateFromParts(set.state);
            const unusable = !allowHaving && state.having.length > 0;
            return (
              <Box as="li" key={set.id} direction="row" gap={2} pad={3}>
                <Ink as="strong" grow breakAnywhere>{set.name}</Ink>
                {set.owned && set.shared && <Badge tone="neutral">{t('bi.sets.sharedTag')}</Badge>}
                {!set.owned && <Badge tone="neutral">{t('bi.sets.byColleague')}</Badge>}
                {set.personal && <Badge tone="warning">{t('bi.personal.tag')}</Badge>}
                <Button size="sm" variant="secondary" onClick={() => onApply(state, set)}>
                  {t('bi.sets.apply')}
                </Button>
                {set.owned && store.canSave && (
                  <>
                    <Button size="sm" variant="ghost" disabled={nothing} onClick={() => void replace(set)}>
                      {t('bi.sets.update')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void share(set)}>
                      {set.shared ? t('bi.sets.unshare') : t('bi.sets.share')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="pencil"
                      onClick={() => {
                        setRenaming(set);
                        setNewName(set.name);
                        setProblem(undefined);
                      }}
                    >
                      {t('bi.sets.rename')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void askDelete(set)}>
                      {t('bi.sets.delete')}
                    </Button>
                  </>
                )}
                {unusable && <Text role="caption" tone="secondary" className="vello-admin-a-fullrow">{t('bi.sets.havingIgnored')}</Text>}
              </Box>
            );
          })}
        </Stack>
      )}
      {store.canSave ? (
        <Measure size="md" gap={3}>
          <Field label={t('bi.sets.name')} htmlFor={`${idPrefix}-set-name`} hint={t('bi.sets.hint')} {...(problem !== undefined && renaming === undefined ? { error: problem } : {})}>
            <TextInput id={`${idPrefix}-set-name`} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </Field>
          <CheckRow id={`${idPrefix}-set-shared`} checked={shared} onChange={setShared}>
            {t('bi.sets.shareWithTeam')}
          </CheckRow>
          <div>
            <Button variant="secondary" loading={busy} disabled={nothing || name.trim() === ''} onClick={() => void save()}>
              {t('bi.sets.save')}
            </Button>
          </div>
        </Measure>
      ) : (
        <Notice tone="neutral">{t('bi.sets.readOnly')}</Notice>
      )}

      <FormDialog open={renaming !== undefined} id={`${idPrefix}-set-rename`} icon="pencil" title={t('bi.sets.rename.title')} body={t('bi.sets.rename.body')} busy={busy} initialFocusId={`${idPrefix}-set-rename-name`} onCancel={() => setRenaming(undefined)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitRename();
          }}
          noValidate
        >
          <Field label={t('bi.sets.name')} htmlFor={`${idPrefix}-set-rename-name`} {...(problem !== undefined ? { error: problem } : {})}>
            <TextInput id={`${idPrefix}-set-rename-name`} value={newName} maxLength={80} onChange={(e) => setNewName(e.target.value)} />
          </Field>
          <Row gap={2} align="stretch">
            <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={() => setRenaming(undefined)}>
              {t('ui.cancel')}
            </Button>
            <Button className="vello-admin-a-btn-grow" type="submit" variant="primary" loading={busy}>
              {t('bi.sets.rename.confirm')}
            </Button>
          </Row>
        </form>
      </FormDialog>
    </Stack>
  );
}
